import { z } from 'zod';
import { chatMentionSchema } from './mentions.js';

export const QueryAnswerCompletenessEnum = z.enum(['complete', 'partial', 'failed']);
export type QueryAnswerCompleteness = z.infer<typeof QueryAnswerCompletenessEnum>;

export const EvaluationOutputSchema = z.object({
  completeness: QueryAnswerCompletenessEnum.describe(
    '"complete" if the answer fully addresses the question, "partial" if it addresses it but is missing pieces, "failed" if it does not answer the question'
  ),
  scopeMatch: z
    .boolean()
    .describe(
      'True if the answer respects the scope of the question (e.g. applied filters, location constraints, entity-specific focus). True by default when no scope was identified.'
    ),
});

export type EvaluationOutput = z.infer<typeof EvaluationOutputSchema>;

export const ConstraintSchema = z.object({
  field: z
    .string()
    .describe('The name of the field or property in camelCase (e.g., "city", "pricePerUnit", "startDate")'),
  op: z.enum(['=', '>', '<', '>=', '<=']).describe('The comparison operator'),
  value: z.unknown().describe('The value to compare against'),
});

export type Constraint = z.infer<typeof ConstraintSchema>;

export const EntitySchema = z.object({
  type: z
    .string()
    .describe(
      'The category or type of the entity (e.g., "person", "organization", "location", "product", "date")'
    ),
  value: z.string().describe('The actual entity value extracted from the text'),
});

export type Entity = z.infer<typeof EntitySchema>;

export const ClassificationOutputSchema = z.object({
  entities: z.array(EntitySchema).describe('Named entities extracted from the conversation'),
  topics: z.array(z.string()).describe('Key topics or themes discussed'),
  constraints: z.array(ConstraintSchema).describe('Filtering or comparison constraints mentioned'),
});

export type ClassificationOutput = z.infer<typeof ClassificationOutputSchema>;

export const queryMetadataSchema = z.object({
  checkpointIdentifier: z.string().optional(),
  classifications: ClassificationOutputSchema.optional(),
  mentions: z.array(chatMentionSchema).optional(),
  evaluation: EvaluationOutputSchema.optional(),
  rewrittenQuery: z.string().optional(),
  retry: z
    .object({
      checkpointIdentifier: z.string(),
    })
    .optional(),
  tokenCount: z.number().int().optional(),
  inputTokenCount: z.number().int().optional(),
  outputTokenCount: z.number().int().optional(),
  model: z.string().optional(),
  durationMs: z.number().int().optional(),
});
export type QueryMetadata = z.infer<typeof queryMetadataSchema>;

export const researchArtifactSourceSchema = z.object({
  id: z.string(),
  summary: z.string(),
  data: z.record(z.string(), z.unknown()),
});
export type ResearchArtifactSource = z.infer<typeof researchArtifactSourceSchema>;

export const researchArtifactSchema = z.object({
  id: z.string(),
  question: z.string(),
  data: z.record(z.string(), z.unknown()),
  sources: z.array(researchArtifactSourceSchema),
});
export type ResearchArtifact = z.infer<typeof researchArtifactSchema>;
